import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import React from 'react';
import {
  getBusStations as getBusStationsDefaultImpl,
  getLocations as getLocationsDefaultImpl
} from '../../../_integrations/everymundo/busDirectory';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Map from '../../controls/MapRC';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import Col from '../../elements/Col';
//-------------------------------------
// Basic components here
//-------------------------------------
import Header from '../../elements/Header';
import Row from '../../elements/Row';
import Text from '../../elements/Text';
import StationItem from '../../items/StationItem/StationItem';
import List from '../../lists/FareListTwoColumns';
import Disclaimer from '../../others/Disclaimer';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior';
import NoResults from '../../others/NoResults';
import SkeletonMobile from '../../others/Skeleton/SkeletonBusDirectoryMapMobile';
import LoadMorePaginator from '../../pagination/LoadMorePaginator';
import withDeepMergeDefaultProps from '../../utils/WithDeepMergeDefaultProps';
import { AutoComplete } from '../BusDirectoryMapContainer/Autocomplete';
import { BusDirectoryProps } from '../BusDirectoryMapContainer/BusDirectoryMapContainer';
import flavors from '../BusDirectoryMapContainer/flavors';
import { PaginationDescription } from '../BusDirectoryMapContainer/PaginationDescription';
import { StationsInterlink } from '../BusDirectoryMapContainer/StationsInterlink';
import { ActionType, useBusDirectory } from '../_commons/busDirectoryHooks';
import { busStationsDefaultPrePopulation } from '../_commons/prepopulationDefaultProps/busStationsDefaultPrePopulation';

function BusDirectoryMapContainerMobile({ components = {}, ...props }: BusDirectoryProps) {
  const {
    translate,
    direction,
    titleKey,
    uiDisplay,
    noStationsBehavior,
    integrationSettings,
    paginationSettings,
    theme,
    enableSEO = true
  } = props;

  const {
    isSkeletonShown,
    errorLoading,
    busStations,
    busStationSelected,
    originData,
    destinationData,
    origin,
    destination,
    selectedOrigin,
    selectedDestination,
    isLoadingOrigins,
    isLoadingDestinations,
    isLoading,
    points,
    errorOrigin,
    errorDestination,
    errorRoute,
    enableRoute,
    handleOnInputChange,
    inputOrigin,
    inputDestination,
    clearOrigin,
    handleOrigin,
    handleDestination,
    handleMarkerOnClick,
    handleTooltipOnClose,
    handleStationItemFocus,
    isPaginationAvailable,
    handlePaginationAction,
    reload,
    clearDestination,
    handleSearch,
    originSelectorRef,
    destinationSelectorRef,
    showNoData,
    getInterlinking,
    dispatch
  } = useBusDirectory(props);

  const showDataSection = selectedOrigin || selectedDestination;

  const containerErrorAlertComponent = (
    <ContainerErrorAlert row={{ marginTop: '2' }} {...components.alert} tryAgain={reload} />
  );

  const headerComponent = (
    <Header
      tag="h2"
      fontSize="6"
      floatLeft
      textTransform="capitalize"
      content={translate(titleKey, 'FIND GREYHOUND STATIONS')}
      {...components.title}
    />
  );

  const interlinkingSection = uiDisplay.showInterlinkingSection ? (
    <StationsInterlink
      links={getInterlinking}
      marginTop="1"
      marginBottom="2"
      {...components.interlinkingSettings}
    />
  ) : null;
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);
  const itemSettings = components.list?.itemSettings ?? {};
  const onStationClick = (station: $TSFixMe, callToActionLabel: string) => {
    dispatch({ type: ActionType.STATION_SELECT, payload: { station, callToActionLabel } });
  };
  itemSettings.onFocus = handleStationItemFocus;
  itemSettings.onStationClick = onStationClick;

  const errorLabelKey = errorRoute
    ? 'cmp-bus-directory-map-route-error-input-selector'
    : 'cmp-bus-directory-map-error-input-selector';

  const handleBook = () => {
    dispatch({
      type: ActionType.FARE_CLICK,
      payload: {
        fare: {
          ...props,
          origin: selectedOrigin?.value,
          destination: selectedDestination?.value
        },
        callToActionLabel: translate('fare_call_to_action_label', 'Book now')
      }
    });
  };

  const handleZoomOnClick = (oldZoom: $TSFixMe, newZoom: $TSFixMe) => {
    dispatch({ type: ActionType.ZOOM, payload: { oldZoom, newZoom } });
  };

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  const renderContent = (
    <Box
      position="relative"
      style={myStyle}
      className={`${className} ${BusDirectoryMapContainerMobile.componentName}`}
    >
      {isLoading && <Loading {...components.loadingSettings} />}
      {uiDisplay.showSkeletonScreen && isSkeletonShown && (
        <SkeletonMobile {...components.skeletonSettings} position="absolute" width="100" />
      )}
      {uiDisplay.showTitle && titleKey && (
        <Row gutters>
          <Col>{headerComponent}</Col>
        </Row>
      )}
      {uiDisplay.showFilters && (
        <>
          <Row gutters {...components.filters} alignItems="end">
            {uiDisplay.showOrigin && (
              <Col
                xs={12}
                sm={uiDisplay.showDestination ? 12 : 9}
                {...components.locationContentSettings}
              >
                <AutoComplete
                  placeholderKey="cmp-bus-directory-map-selector-placeholder"
                  labelKey="origin"
                  {...components.locationSelector}
                  options={originData}
                  value={inputOrigin === undefined ? origin && origin.value : inputOrigin}
                  onChange={handleOrigin}
                  onInputChange={(value: $TSFixMe) => handleOnInputChange(value, 'origin')}
                  hasError={errorOrigin}
                  inputFocusReferences={originSelectorRef}
                  dataTestId="originSelector"
                  onClear={clearOrigin}
                  loading={isLoadingOrigins}
                />
              </Col>
            )}
            {uiDisplay.showDestination && (
              <Col
                xs={12}
                sm={uiDisplay.showOrigin ? 12 : 9}
                paddingTop="2" // TODO : show Label
                {...components.destinationContentSettings}
              >
                <AutoComplete
                  placeholderKey="cmp-bus-directory-map-selector-placeholder"
                  labelKey="destination"
                  {...components.locationSelector}
                  {...components.destinationSelector}
                  options={destinationData}
                  value={
                    inputDestination === undefined
                      ? destination && destination.value
                      : inputDestination
                  }
                  onChange={handleDestination}
                  onInputChange={(value: $TSFixMe) => handleOnInputChange(value, 'destination')}
                  hasError={errorDestination || errorRoute}
                  inputFocusReferences={destinationSelectorRef}
                  dataTestId="destinationSelector"
                  onClear={clearDestination}
                  loading={isLoadingDestinations}
                />
              </Col>
            )}
            <Col
              xs={12}
              sm={3}
              paddingTop="2"
              marginLeft="auto"
              {...components.searchContentSettings}
            >
              <Button
                rounded="@theme:button.rounded"
                color="primary"
                width="100"
                padding="10px"
                {...components.search}
                onClick={handleSearch}
                cursor={isLoading ? 'default' : 'pointer'}
                disabled={isLoading}
              >
                {translate('cmp-bus-directory-map-button-search', 'Search')}
              </Button>
            </Col>
          </Row>
          {(errorOrigin || errorDestination || errorRoute) && (
            <Text textColor="danger" {...components.errorLabel}>
              {translate(errorLabelKey, errorLabelKey)}
            </Text>
          )}
        </>
      )}
      {showDataSection && (
        <>
          {showNoData ? (
            <Row marginTop="2" gutters>
              {noStationsBehavior === 'showNoResults' && (
                <Col>
                  <NoResults flavor="mobile" onClick={reload} {...components.noResults} />
                </Col>
              )}
              {noStationsBehavior === 'hidden' && <NoFaresBehavior />}
            </Row>
          ) : (
            !errorLoading && (
              <Row marginTop="2" gutters>
                <Col lg={12}>
                  {!isSkeletonShown && (
                    <Map
                      isMobile
                      overlayLabelKey="cmp-map-mobile-activate-interactive-message"
                      heightContainer="300px"
                      {...components.map}
                      onClickZoom={handleZoomOnClick}
                      onClickMarker={handleMarkerOnClick}
                      onCloseTooltip={handleTooltipOnClose}
                      accessToken={integrationSettings.v5MapBoxApi.token}
                      enableRoute={enableRoute}
                      points={points}
                      errorFallback={() => containerErrorAlertComponent}
                      onStationClick={onStationClick}
                    />
                  )}
                </Col>
                <Col lg={12}>
                  {uiDisplay.showPaginationResultsLabel && (
                    <PaginationDescription
                      paddingTop="2"
                      origin={selectedOrigin?.label}
                      destination={selectedDestination?.label}
                      enableRoute={enableRoute}
                      resultsCount={points?.length}
                      {...components.descriptionPaginationSettings}
                    />
                  )}
                </Col>
                <Col lg={12}>
                  <List
                    oneColumn={true}
                    {...components.list}
                    style={{
                      alignContent: 'flex-start',
                      padding: '1px',
                      ...components.list?.style
                    }}
                    selectedItemSettings={{
                      borderColor: 'primary',
                      border: true,
                      ...components.list?.selectedItemSettings
                    }}
                    itemSettings={itemSettings}
                    ItemComponentInstance={StationItem}
                    focusOn={busStationSelected ? busStationSelected.index : null}
                    itemsData={busStations}
                  />
                </Col>
              </Row>
            )
          )}
          {errorLoading && containerErrorAlertComponent}
          {uiDisplay.showLoadMorePaginator && isPaginationAvailable() && (
            <Box
              textAlign="center"
              marginTop="2"
              marginBottom="2"
              {...components.paginationContentSettings}
            >
              <LoadMorePaginator
                isLoading={isLoading}
                disabled={busStations.length >= (paginationSettings.limit as number)}
                onClickAction={handlePaginationAction}
                {...components.pagination}
                buttonSetting={{
                  outline: true,
                  color: 'primary',
                  rounded: '@theme:button.rounded',
                  paddingWidth: '30px',
                  paddingHeight: '1',
                  ...components.pagination?.buttonSetting
                }}
              />
            </Box>
          )}
          {!errorLoading && uiDisplay.showBookNow && (
            <Box textAlign="center" {...components.bookNowContentSettings}>
              <Button
                color="primary"
                textTransform="uppercase"
                rounded="@theme:button.rounded"
                paddingWidth="30px"
                paddingHeight="1"
                marginTop="1"
                onClick={handleBook}
                {...components.bookNowSettings}
              >
                {translate('fare_call_to_action_label', 'Book now')}
              </Button>
            </Box>
          )}
          {interlinkingSection && (
            <Row gutters>
              <Col>{interlinkingSection}</Col>
            </Row>
          )}
          {uiDisplay.showDisclaimer && (
            <Row gutters>
              <Col>
                <Disclaimer
                  marginTop="2"
                  marginBottom="2"
                  floatLeft
                  labelKey="cmp-bus-directory-map-disclaimer"
                  {...components.disclaimer}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Box>
  );

  return renderContent;
}

BusDirectoryMapContainerMobile.defaultProps = {
  fareItemInteractionType: 'popup',
  uiDisplay: {
    showDisclaimer: true,
    showSkeletonScreen: true,
    showTitle: true,
    showBookNow: false,
    showOrigin: true,
    showDestination: false,
    showFilters: true,
    showLoadMorePaginator: true,
    showInterlinkingSection: true,
    showPaginationResultsLabel: true
  },
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareBusDirectoryMap'
  },
  titleKey: 'cmp-bus-directory-title-key',
  initialBusStations: null,
  getBusStations: getBusStationsDefaultImpl,
  getLocations: getLocationsDefaultImpl,
  noStationsBehavior: 'showNoResults',
  prePopulation: busStationsDefaultPrePopulation,
  flavor: '',
  paginationSettings: {
    step: 10,
    limit: 30,
    initialVisibleItems: 10
  },
  interlinkingBaseUrl: null,
  directionsBaseUrl: 'https://www.google.com/maps/dir/Current+Location',
  pageContext: {
    pageType: 'ci',
    languageCode: 'en',
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  localizationData: null,
  theme: null
};
BusDirectoryMapContainerMobile.componentName = 'BusDirectoryMapContainerMobile';

BusDirectoryMapContainerMobile.flavors = flavors;

export default withDeepMergeDefaultProps(BusDirectoryMapContainerMobile);
